/* ══════════════════ Rift Simulator — Core Types ══════════════════ */

export type MinionType = 'melee' | 'caster' | 'siege' | 'super';
export type Team = 'blue' | 'red';
export type Lane = 'top' | 'mid' | 'bot';

/* ── Minion base stats ── */
export interface MinionStats {
  maxHealth: number;
  attackDamage: number;
  /** Attacks per second */
  attackSpeed: number;
  /** Attack range in game units */
  attackRange: number;
  /** Movement speed in game units per second */
  moveSpeed: number;
  armor: number;
  magicResist: number;
}

/* ── A single minion on the map ── */
export interface Minion {
  id: number;
  type: MinionType;
  team: Team;
  lane: Lane;
  /** Position in grid coordinates (0–511) */
  x: number;
  y: number;
  health: number;
  maxHealth: number;
  stats: MinionStats;
  /** Index of the next lane-path waypoint this minion is heading toward */
  waypointIdx: number;
  /** ID of the current enemy minion target (null ⇒ no minion target) */
  targetId: number | null;
  /** ID of the current tower target (null ⇒ no tower target) */
  towerTargetId: string | null;
  /** Seconds until next auto-attack */
  attackCooldown: number;
  alive: boolean;
  /** Which wave spawned this minion (1-indexed) */
  waveNumber: number;
  /** Perpendicular offset from lane centre-line (grid cells) for natural spread */
  laneOffset: number;
}

/* ── Queued minion waiting to spawn ── */
export interface QueuedMinion {
  /** Game-time at which this minion actually appears on the map */
  spawnAt: number;
  type: MinionType;
  team: Team;
  lane: Lane;
  waveNumber: number;
}

/* ── Full simulation snapshot ── */
export interface SimulationState {
  /** Game time in seconds (starts at 0) */
  gameTime: number;
  /** All living minions on the map */
  minions: Minion[];
  /** All towers on the map */
  towers: Tower[];
  /** All jungle camps on the map */
  jungleCamps: JungleCamp[];
  /** Minions waiting to spawn (staggered within a wave) */
  spawnQueue: QueuedMinion[];
  /** Auto-incrementing minion id counter */
  nextMinionId: number;
  /** Wave counter per team (shared across lanes — waves go to all 3 lanes) */
  waveCounter: Record<Team, number>;
  /** Timestamp of next wave spawn for each team */
  nextWaveTime: Record<Team, number>;
  /** Is the sim running? */
  running: boolean;
  /** Speed multiplier (1 = real-time) */
  speed: number;
}

/* ── Lane path: sequence of grid-space waypoints ── */
export interface LanePath {
  waypoints: { x: number; y: number }[];
}

/* ── Constants ── */
export const GRID_RES = 512;
export const MAP_GAME_UNITS = 16000;
/** Game-units per grid cell */
export const CELL_SIZE = MAP_GAME_UNITS / GRID_RES; // 31.25
/** Minion acquisition range in game-units (Wiki: 500) */
export const ACQUISITION_RANGE = 500;
/** Same in grid cells */
export const ACQUISITION_RANGE_GRID = ACQUISITION_RANGE / CELL_SIZE; // ≈ 16
/** First wave spawns at this game-time (seconds) */
export const FIRST_WAVE_TIME = 30; // 0:30
/** Seconds between each individual minion spawn within a wave */
export const MINION_SPAWN_INTERVAL = 0.792;

/* ══════════════════ Towers ══════════════════ */

export type TowerTier = 'outer' | 'inner' | 'inhib' | 'nexus';

export interface TowerStats {
  maxHealth: number;
  attackDamage: number;
  /** Attacks per second */
  attackSpeed: number;
  /** Attack range in grid cells */
  attackRange: number;
  armor: number;
  magicResist: number;
}

export interface Tower {
  id: string;
  team: Team;
  lane: Lane | 'base';
  tier: TowerTier;
  /** Grid position (0–511) */
  gx: number;
  gy: number;
  health: number;
  maxHealth: number;
  stats: TowerStats;
  /** ID of the current target minion (null ⇒ idle) */
  targetId: number | null;
  /** Seconds until next auto-attack */
  attackCooldown: number;
  alive: boolean;
}

/** Tower attack range in game units (750 for all turrets in LoL) */
export const TOWER_RANGE_UNITS = 750;
/** Tower attack range in grid cells */
export const TOWER_RANGE_GRID = TOWER_RANGE_UNITS / CELL_SIZE; // ≈ 24

/* ══════════════════ Jungle Camps ══════════════════ */

export type JungleCampType =
  | 'blue_buff'
  | 'red_buff'
  | 'gromp'
  | 'wolves'
  | 'raptors'
  | 'krugs'
  | 'scuttle'
  | 'dragon'
  | 'void_grubs'
  | 'rift_herald'
  | 'baron';

export interface JungleCamp {
  id: string;
  type: JungleCampType;
  side: 'blue' | 'red' | 'neutral';
  name: string;
  /** Grid position (0–511) */
  gx: number;
  gy: number;
  /** Game-time when the camp first spawns (seconds) */
  initialSpawn: number;
  /** Seconds after killed before the camp respawns */
  respawnTime: number;
  /** Key into jungleCampImages for the icon */
  imageKey: string;

  /* ── Runtime state ── */
  alive: boolean;
  /** Game-time when the camp next spawns / respawns */
  nextSpawnAt: number;
  /** Game-time when last killed (null if never killed) */
  killedAt: number | null;
}

/* ══════════════════ Champions (player-controlled markers) ══════════════════ */

export type ChampionRole = 'top' | 'jungle' | 'mid' | 'adc' | 'support';

export interface SimChampion {
  id: number;
  name: string;
  team: Team;
  role: ChampionRole;
  /** Grid position (0–511) */
  gx: number;
  gy: number;
  /** Movement speed in game-units / sec (default 350) */
  moveSpeed: number;
  /** Hex colour for the marker */
  color: string;
}